/**
 * Messaging Service - Fast, reliable messaging with E2E encryption
 * Messages are stored encrypted in Firestore (no IPFS)
 */
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  deleteDoc,
  increment,
  Timestamp
} from 'firebase/firestore';
import { 
  encryptMessage, 
  decryptMessage, 
  generateSharedMessageKey 
} from '../cryptoOperations';
import { getUsernameFromWallet, getWalletFromUsername } from '../userMappingService';

// Normalize wallet address
const normalize = (addr) => {
  if (!addr) return null;
  const str = addr.toLowerCase();
  return str.startsWith('0x') ? str : '0x' + str;
};

/**
 * Generate conversation ID from two wallet addresses
 * @param {string} address1 - First wallet address
 * @param {string} address2 - Second wallet address
 * @returns {string} Deterministic conversation ID
 */
export const getConversationId = (address1, address2) => {
  const addr1 = normalize(address1);
  const addr2 = normalize(address2);
  if (!addr1 || !addr2) throw new Error('Both addresses required');
  const sorted = [addr1, addr2].sort();
  return `conv_${sorted[0]}_${sorted[1]}`;
};

/**
 * Get the other party's address from conversation ID
 * @param {string} conversationId - The conversation ID
 * @param {string} myAddress - My wallet address
 * @returns {string} The other party's address
 */
export const getOtherPartyFromConversationId = (conversationId, myAddress) => {
  if (!conversationId || !conversationId.startsWith('conv_')) return null;
  const parts = conversationId.replace('conv_', '').split('_');
  if (parts.length !== 2) return null;
  const me = normalize(myAddress);
  return parts[0] === me ? parts[1] : parts[0];
};

// Shared key cache
const sharedKeyCache = new Map();

/**
 * Get or create shared encryption key for a conversation
 */
const getSharedKey = async (myAddress, otherAddress) => {
  const cacheKey = getConversationId(myAddress, otherAddress);
  if (sharedKeyCache.has(cacheKey)) {
    return sharedKeyCache.get(cacheKey);
  }
  const key = await generateSharedMessageKey(myAddress, otherAddress);
  sharedKeyCache.set(cacheKey, key);
  return key;
};

/**
 * Send an encrypted message
 * @param {string} senderAddress - Sender's wallet address
 * @param {string} recipientAddress - Recipient's wallet address  
 * @param {string} text - Plain text message
 * @returns {Promise<Object>} Sent message data
 */
export const sendMessage = async (senderAddress, recipientAddress, text) => {
  const sender = normalize(senderAddress);
  const recipient = normalize(recipientAddress);
  
  if (!sender || !recipient) {
    throw new Error('Sender and recipient addresses required');
  }
  
  if (sender === recipient) {
    throw new Error('Cannot send message to yourself');
  }
  
  if (!text || typeof text !== 'string' || text.trim().length === 0) {
    throw new Error('Message text required');
  }
  
  const trimmedText = text.trim();
  if (trimmedText.length > 10000) {
    throw new Error('Message too long (max 10000 characters)');
  }
  
  // Get shared encryption key
  const sharedKey = await getSharedKey(sender, recipient);
  
  // Encrypt the message
  const encrypted = encryptMessage(trimmedText, sharedKey);
  
  // Generate IDs
  const conversationId = getConversationId(sender, recipient);
  const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const timestamp = new Date();
  
  // Save encrypted message to Firestore
  const messageData = {
    messageId,
    conversationId,
    sender,
    recipient,
    ciphertext: encrypted.ciphertext,
    nonce: encrypted.nonce,
    timestamp: Timestamp.fromDate(timestamp),
    read: false
  };
  
  const messagesRef = collection(db, 'messages');
  await setDoc(doc(messagesRef, messageId), messageData);
  
  // Update conversation metadata for both parties
  const batch = writeBatch(db);
  const conversationsRef = collection(db, 'conversations');
  
  // Create message preview (truncate to 50 chars)
  const lastMessagePreview = trimmedText.length > 50 
    ? trimmedText.substring(0, 50) + '...' 
    : trimmedText;
  
  // For sender
  batch.set(doc(conversationsRef, `${conversationId}_${sender}`), {
    conversationId,
    participant: sender,
    otherParty: recipient,
    lastMessageTime: Timestamp.fromDate(timestamp),
    lastMessagePreview: lastMessagePreview,
    lastMessageSender: sender,
    hasMessages: true,
    updatedAt: serverTimestamp()
  }, { merge: true });
  
  // For recipient - increment unread count
  batch.set(doc(conversationsRef, `${conversationId}_${recipient}`), {
    conversationId,
    participant: recipient,
    otherParty: sender,
    lastMessageTime: Timestamp.fromDate(timestamp),
    lastMessagePreview: lastMessagePreview,
    lastMessageSender: sender,
    hasMessages: true,
    unreadCount: increment(1),
    updatedAt: serverTimestamp()
  }, { merge: true });
  
  // Update global unread count for recipient (for badge)
  batch.set(doc(collection(db, 'unreadCounts'), recipient), {
    unreadCount: increment(1),
    updatedAt: serverTimestamp()
  }, { merge: true });
  
  await batch.commit();
  
  // Clean up any duplicate/old conversation documents for sender
  // (in case there are old documents with different ID formats)
  try {
    const q = query(
      conversationsRef,
      where('participant', '==', sender),
      where('otherParty', '==', recipient)
    );
    const snapshot = await getDocs(q);
    
    // Delete any documents that don't match the correct format
    const correctDocId = `${conversationId}_${sender}`;
    for (const docSnap of snapshot.docs) {
      if (docSnap.id !== correctDocId) {
        await deleteDoc(docSnap.ref);
        console.log('[Messaging] Cleaned up old conversation doc:', docSnap.id);
      }
    }
  } catch (cleanupError) {
    console.warn('[Messaging] Cleanup of old conversations failed:', cleanupError);
    // Non-critical, continue
  }
  
  // Return decrypted message for local display
  return {
    messageId,
    conversationId,
    sender,
    recipient,
    text: trimmedText,
    timestamp,
    read: false,
    isMine: true
  };
};

/**
 * Load messages for a conversation
 * @param {string} myAddress - My wallet address
 * @param {string} otherAddress - Other party's wallet address
 * @param {number} messageLimit - Max messages to load
 * @returns {Promise<Array>} Decrypted messages
 */
export const loadMessages = async (myAddress, otherAddress, messageLimit = 50) => {
  const me = normalize(myAddress);
  const other = normalize(otherAddress);
  
  console.log('[Messaging] loadMessages called:', { me, other });
  
  if (!me || !other) {
    console.warn('[Messaging] Invalid addresses:', { me, other });
    return [];
  }
  
  const conversationId = getConversationId(me, other);
  console.log('[Messaging] Generated conversationId:', conversationId);
  
  let sharedKey;
  try {
    sharedKey = await getSharedKey(me, other);
    console.log('[Messaging] Got shared key');
  } catch (keyError) {
    console.error('[Messaging] Failed to get shared key:', keyError);
    return [];
  }
  
  // Simple query without orderBy to avoid index requirement
  const messagesRef = collection(db, 'messages');
  const q = query(
    messagesRef,
    where('conversationId', '==', conversationId),
    limit(messageLimit)
  );
  
  const snapshot = await getDocs(q);
  console.log('[Messaging] Found messages in Firestore:', snapshot.docs.length);
  
  const messages = [];
  let decryptionErrors = 0;
  
  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    try {
      const decryptedText = decryptMessage({
        ciphertext: data.ciphertext,
        nonce: data.nonce
      }, sharedKey);
      
      messages.push({
        messageId: data.messageId,
        conversationId: data.conversationId,
        sender: data.sender,
        recipient: data.recipient,
        text: decryptedText,
        timestamp: data.timestamp?.toDate() || new Date(),
        read: data.read || false,
        isMine: data.sender === me
      });
    } catch (error) {
      decryptionErrors++;
      console.error('[Messaging] Failed to decrypt message:', {
        messageId: data.messageId,
        sender: data.sender,
        recipient: data.recipient,
        storedConversationId: data.conversationId,
        queriedConversationId: conversationId,
        error: error.message
      });
    }
  }
  
  console.log('[Messaging] Decrypted messages:', messages.length, 'Errors:', decryptionErrors);
  
  // Sort oldest first
  messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  
  return messages;
};

/**
 * Subscribe to new messages in a conversation (real-time)
 * @param {string} myAddress - My wallet address
 * @param {string} otherAddress - Other party's wallet address
 * @param {Function} onMessage - Callback for new messages
 * @returns {Function} Unsubscribe function
 */
export const subscribeToMessages = (myAddress, otherAddress, onMessage) => {
  const me = normalize(myAddress);
  const other = normalize(otherAddress);
  
  if (!me || !other) return () => {};
  
  const conversationId = getConversationId(me, other);
  
  let sharedKey = null;
  let isInitialized = false;
  let pendingSnapshots = []; // Queue snapshots until key is ready
  let unsubscribeFirestore = null;
  
  const processedIds = new Set();
  
  // Process a single message
  const processMessage = (data) => {
    if (!sharedKey) return;
    if (processedIds.has(data.messageId)) return;
    processedIds.add(data.messageId);
    
    try {
      const decryptedText = decryptMessage({
        ciphertext: data.ciphertext,
        nonce: data.nonce
      }, sharedKey);
      
      onMessage({
        messageId: data.messageId,
        conversationId: data.conversationId,
        sender: data.sender,
        recipient: data.recipient,
        text: decryptedText,
        timestamp: data.timestamp?.toDate() || new Date(),
        read: data.read || false,
        isMine: data.sender === me
      });
    } catch (error) {
      console.warn('[Messaging] Failed to decrypt message:', data.messageId, error.message);
    }
  };
  
  // Process pending messages once key is ready
  const processPendingSnapshots = () => {
    if (!sharedKey) return;
    for (const snapshot of pendingSnapshots) {
      for (const change of snapshot.docChanges()) {
        if (change.type === 'added') {
          processMessage(change.doc.data());
        }
      }
    }
    pendingSnapshots = [];
  };
  
  // Get shared key first
  getSharedKey(me, other).then(key => {
    sharedKey = key;
    isInitialized = true;
    // Process any snapshots that arrived before key was ready
    processPendingSnapshots();
  }).catch(err => {
    console.error('[Messaging] Failed to get shared key:', err);
  });
  
  // Set up Firestore listener
  const messagesRef = collection(db, 'messages');
  const q = query(
    messagesRef,
    where('conversationId', '==', conversationId),
    limit(100)
  );
  
  unsubscribeFirestore = onSnapshot(q, (snapshot) => {
    if (!isInitialized || !sharedKey) {
      // Queue snapshot until key is ready
      pendingSnapshots.push(snapshot);
      return;
    }
    
    for (const change of snapshot.docChanges()) {
      if (change.type === 'added') {
        processMessage(change.doc.data());
      }
    }
  }, (error) => {
    console.error('[Messaging] Subscription error:', error);
  });
  
  return () => {
    if (unsubscribeFirestore) unsubscribeFirestore();
  };
};

/**
 * Load all conversations for a user
 * @param {string} myAddress - My wallet address
 * @returns {Promise<Array>} Conversations with metadata
 */
export const loadConversations = async (myAddress) => {
  const me = normalize(myAddress);
  if (!me) return [];
  
  try {
    const conversationsRef = collection(db, 'conversations');
    // Simple query without orderBy to avoid index requirement
    const q = query(
      conversationsRef,
      where('participant', '==', me)
    );
    
    const snapshot = await getDocs(q);
    const conversations = [];
    
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      
      // Try to get username for other party
      let displayName = null;
      try {
        const username = await getUsernameFromWallet(data.otherParty);
        if (username) displayName = username;
      } catch (e) {
        // Username lookup failed
      }
      
      conversations.push({
        conversationId: data.conversationId,
        otherParty: data.otherParty,
        displayName,
        lastMessageTime: data.lastMessageTime?.toDate() || null,
        unreadCount: data.unreadCount || 0
      });
    }
    
    // Sort by lastMessageTime client-side
    conversations.sort((a, b) => {
      const timeA = a.lastMessageTime?.getTime() || 0;
      const timeB = b.lastMessageTime?.getTime() || 0;
      return timeB - timeA;
    });
    
    return conversations;
  } catch (error) {
    console.error('[Messaging] Error loading conversations:', error);
    return [];
  }
};

/**
 * Subscribe to conversations (real-time updates)
 * @param {string} myAddress - My wallet address
 * @param {Function} onUpdate - Callback with updated conversations
 * @returns {Function} Unsubscribe function
 */
export const subscribeToConversations = (myAddress, onUpdate) => {
  const me = normalize(myAddress);
  if (!me) return () => {};
  
  const conversationsRef = collection(db, 'conversations');
  // Simple query without orderBy to avoid index requirement
  const q = query(
    conversationsRef,
    where('participant', '==', me)
  );
  
  return onSnapshot(q, async (snapshot) => {
    // Deduplicate by otherParty address to prevent duplicate conversations
    // (in case old documents exist with different conversationId formats)
    const conversationsMap = new Map();
    
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const otherPartyNormalized = data.otherParty?.toLowerCase();
      
      if (!otherPartyNormalized) continue;
      
      // If we already have a conversation with this person, keep the one with more recent activity
      const existing = conversationsMap.get(otherPartyNormalized);
      if (existing) {
        const existingTime = existing.lastMessageTime?.getTime() || 0;
        const newTime = data.lastMessageTime?.toDate()?.getTime() || 0;
        // Keep the more recent conversation
        if (existingTime >= newTime) continue;
      }
      
      // Try to get username for other party
      let displayName = null;
      try {
        const username = await getUsernameFromWallet(data.otherParty);
        if (username) displayName = username;
      } catch (e) {
        // Username lookup failed
      }
      
      // Generate proper conversationId (in case old ones have different format)
      const properConversationId = getConversationId(me, otherPartyNormalized);
      
      const conversationObj = {
        conversationId: properConversationId,
        otherParty: otherPartyNormalized,
        displayName,
        lastMessageTime: data.lastMessageTime?.toDate() || null,
        lastMessagePreview: data.lastMessagePreview || null,
        lastMessageSender: data.lastMessageSender || null,
        hasMessages: data.hasMessages || false,
        unreadCount: data.unreadCount || 0
      };
      
      console.log('[Messaging] Conversation from Firestore:', {
        otherParty: otherPartyNormalized,
        hasMessages: conversationObj.hasMessages,
        lastMessagePreview: conversationObj.lastMessagePreview,
        unreadCount: conversationObj.unreadCount,
        firestoreData: {
          hasMessages: data.hasMessages,
          lastMessagePreview: data.lastMessagePreview,
          unreadCount: data.unreadCount
        }
      });
      
      conversationsMap.set(otherPartyNormalized, conversationObj);
    }
    
    const conversations = Array.from(conversationsMap.values());
    
    // Sort by lastMessageTime client-side
    conversations.sort((a, b) => {
      const timeA = a.lastMessageTime?.getTime() || 0;
      const timeB = b.lastMessageTime?.getTime() || 0;
      return timeB - timeA;
    });
    
    onUpdate(conversations);
  }, (error) => {
    console.error('[Messaging] Conversations subscription error:', error);
    // Still call onUpdate with empty array to stop loading state
    onUpdate([]);
  });
};

/**
 * Subscribe to unread message count (for badge)
 * @param {string} myAddress - My wallet address
 * @param {Function} onUpdate - Callback with unread count
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUnreadCount = (myAddress, onUpdate) => {
  const me = normalize(myAddress);
  if (!me) {
    onUpdate(0);
    return () => {};
  }
  
  const unreadRef = doc(collection(db, 'unreadCounts'), me);
  
  return onSnapshot(unreadRef, (docSnap) => {
    if (docSnap.exists()) {
      onUpdate(docSnap.data().unreadCount || 0);
    } else {
      onUpdate(0);
    }
  }, (error) => {
    console.error('[Messaging] Unread count subscription error:', error);
    onUpdate(0);
  });
};

/**
 * Mark messages as read in a conversation
 * @param {string} myAddress - My wallet address
 * @param {string} otherAddress - Other party's wallet address
 */
export const markConversationAsRead = async (myAddress, otherAddress) => {
  const me = normalize(myAddress);
  const other = normalize(otherAddress);
  
  if (!me || !other) return;
  
  const conversationId = getConversationId(me, other);
  
  try {
    // Get unread messages
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('conversationId', '==', conversationId),
      where('recipient', '==', me),
      where('read', '==', false)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return;
    
    const batch = writeBatch(db);
    let unreadCount = 0;
    
    snapshot.forEach(docSnap => {
      batch.update(docSnap.ref, { read: true });
      unreadCount++;
    });
    
    // Reset conversation unread count
    const convRef = doc(collection(db, 'conversations'), `${conversationId}_${me}`);
    batch.set(convRef, { unreadCount: 0 }, { merge: true });
    
    // Decrement global unread count
    const unreadCountRef = doc(collection(db, 'unreadCounts'), me);
    batch.set(unreadCountRef, {
      unreadCount: increment(-unreadCount),
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    await batch.commit();
  } catch (error) {
    console.error('[Messaging] Error marking as read:', error);
  }
};

/**
 * Delete a conversation and all messages in it
 * @param {string} myAddress - My wallet address  
 * @param {string} otherAddress - Other party's wallet address
 */
export const deleteConversation = async (myAddress, otherAddress) => {
  const me = normalize(myAddress);
  const other = normalize(otherAddress);
  
  if (!me || !other) return;
  
  const conversationId = getConversationId(me, other);
  
  // Delete all messages in the conversation
  const messagesRef = collection(db, 'messages');
  const q = query(messagesRef, where('conversationId', '==', conversationId));
  const snapshot = await getDocs(q);
  
  const batch = writeBatch(db);
  
  // Delete all messages
  snapshot.forEach(docSnap => {
    batch.delete(docSnap.ref);
  });
  
  // Delete conversation entries for both parties
  const convRef = doc(collection(db, 'conversations'), `${conversationId}_${me}`);
  batch.delete(convRef);
  
  // Also try to delete the other party's conversation entry (in case they haven't seen it)
  const otherConvRef = doc(collection(db, 'conversations'), `${conversationId}_${other}`);
  batch.delete(otherConvRef);
  
  await batch.commit();
};

/**
 * Check if a conversation has any messages (to determine if it's a new conversation)
 * @param {string} myAddress - My wallet address
 * @param {string} otherAddress - Other party's wallet address
 * @returns {Promise<boolean>} True if conversation has messages
 */
export const hasExistingMessages = async (myAddress, otherAddress) => {
  const me = normalize(myAddress);
  const other = normalize(otherAddress);
  
  if (!me || !other) return false;
  
  const conversationId = getConversationId(me, other);
  const messagesRef = collection(db, 'messages');
  const q = query(messagesRef, where('conversationId', '==', conversationId), limit(1));
  const snapshot = await getDocs(q);
  
  const hasMessages = !snapshot.empty;
  console.log('[Messaging] hasExistingMessages check:', { 
    me, 
    other, 
    conversationId, 
    hasMessages,
    docCount: snapshot.docs.length 
  });
  
  return hasMessages;
};

/**
 * Block a user
 * @param {string} myAddress - My wallet address
 * @param {string} blockedAddress - Address to block
 */
export const blockUser = async (myAddress, blockedAddress) => {
  const me = normalize(myAddress);
  const blocked = normalize(blockedAddress);
  
  if (!me || !blocked || me === blocked) return;
  
  const blocksRef = collection(db, 'blocks');
  await setDoc(doc(blocksRef, `${me}_${blocked}`), {
    blocker: me,
    blocked: blocked,
    timestamp: serverTimestamp()
  });
};

/**
 * Unblock a user
 * @param {string} myAddress - My wallet address
 * @param {string} blockedAddress - Address to unblock
 */
export const unblockUser = async (myAddress, blockedAddress) => {
  const me = normalize(myAddress);
  const blocked = normalize(blockedAddress);
  
  if (!me || !blocked) return;
  
  const blocksRef = collection(db, 'blocks');
  await deleteDoc(doc(blocksRef, `${me}_${blocked}`));
};

/**
 * Check if user is blocked
 * @param {string} myAddress - My wallet address
 * @param {string} otherAddress - Other user's address
 * @returns {Promise<boolean>} True if blocked (either direction)
 */
export const isBlocked = async (myAddress, otherAddress) => {
  const me = normalize(myAddress);
  const other = normalize(otherAddress);
  
  if (!me || !other) return false;
  
  try {
    const blocksRef = collection(db, 'blocks');
    
    const [doc1, doc2] = await Promise.all([
      getDoc(doc(blocksRef, `${me}_${other}`)),
      getDoc(doc(blocksRef, `${other}_${me}`))
    ]);
    
    return doc1.exists() || doc2.exists();
  } catch (error) {
    console.error('[Messaging] Error checking block status:', error);
    return false;
  }
};

/**
 * Search usernames for autocomplete
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Matching usernames
 */
export const searchUsernames = async (searchTerm) => {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return [];
  }
  
  // Don't search if it looks like a wallet address
  if (searchTerm.startsWith('0x') && searchTerm.length >= 10) {
    return [];
  }
  
  try {
    // Strip "@" prefix if present
    let term = searchTerm.toLowerCase().trim();
    if (term.startsWith('@')) {
      term = term.substring(1);
    }
    
    // Search in profiles collection
    const profilesRef = collection(db, 'profiles');
    const snapshot = await getDocs(query(profilesRef, limit(500)));
    
    const suggestions = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const username = data.username;
      if (username && username.toLowerCase().startsWith(term)) {
        suggestions.push({
          username: username,
          name: data.name || username,
          avatar: data.avatar || data.avatarPreview || null
        });
      }
    });
    
    // Sort by username and limit to 10
    suggestions.sort((a, b) => a.username.localeCompare(b.username));
    return suggestions.slice(0, 10);
  } catch (error) {
    console.error('[Messaging] Error searching usernames:', error);
    return [];
  }
};

export default {
  sendMessage,
  loadMessages,
  subscribeToMessages,
  loadConversations,
  subscribeToConversations,
  subscribeToUnreadCount,
  markConversationAsRead,
  deleteConversation,
  blockUser,
  unblockUser,
  isBlocked,
  searchUsernames,
  getConversationId,
  getOtherPartyFromConversationId
};
